import { useNode } from "@craftjs/core";
import React, { ReactNode } from "react";
import { Box } from "@mui/material";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { SelectField, SliderField, CheckboxField, ColorField, SettingsSection, SettingsRow } from "../ui/form/SettingsField";

export interface NavbarContainerProps {
  children?: ReactNode;
  backgroundColor?: string;
  textColor?: string;
  sticky?: boolean;
  maxWidth?: string;
  paddingX?: number;
  paddingY?: number;
  gap?: number;
}

// NavbarContainer - Simple container with wrap support
// Use this for flexible navbar layouts that wrap on mobile
export const NavbarContainer = ({
  children,
  backgroundColor = "#ffffff",
  textColor = "#1f2937",
  sticky = true,
  maxWidth = "1280px",
  paddingX = 24,
  paddingY = 12,
  gap = 16,
}: NavbarContainerProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  return (
    <Box
      component="nav"
      ref={(ref: HTMLElement | null) => {
        if (ref) connect(drag(ref));
      }}
      sx={{
        width: "100%",
        maxWidth: maxWidth,
        mx: "auto",
        px: `${paddingX}px`,
        py: `${paddingY}px`,
        bgcolor: backgroundColor,
        color: textColor,
        position: sticky ? "sticky" : "relative",
        top: sticky ? 0 : "auto",
        zIndex: 1000,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: `${gap}px`,
        minHeight: "40px",
        flexWrap: "wrap", // Wrap on mobile
        ...(selected && { ...selectedOutlineSx, outlineOffset: "-2px" }),
      }}
    >
      {children}
    </Box>
  );
};

export const NavbarContainerSettings = () => {
  const {
    actions: { setProp },
    backgroundColor,
    textColor,
    sticky,
    maxWidth,
    paddingX,
    paddingY,
    gap,
  } = useNode((node) => ({
    backgroundColor: node.data.props.backgroundColor,
    textColor: node.data.props.textColor,
    sticky: node.data.props.sticky,
    maxWidth: node.data.props.maxWidth,
    paddingX: node.data.props.paddingX,
    paddingY: node.data.props.paddingY,
    gap: node.data.props.gap,
  }));

  return (
    <SettingsSection>
      <CheckboxField
        label="Sticky Navigation"
        checked={sticky}
        onChange={(v) => setProp((props: NavbarContainerProps) => (props.sticky = v))}
      />

      <SelectField
        label="Max Width"
        value={maxWidth}
        onChange={(v) => setProp((props: NavbarContainerProps) => (props.maxWidth = v))}
        options={[
          { value: "100%", label: "Full Width" },
          { value: "1280px", label: "1280px" },
          { value: "1024px", label: "1024px" },
          { value: "768px", label: "768px" },
        ]}
      />

      <SettingsRow>
        <SliderField
          label="Padding X"
          value={paddingX}
          onChange={(v) => setProp((props: NavbarContainerProps) => (props.paddingX = v))}
          min={0}
          max={48}
          unit="px"
        />
        <SliderField
          label="Padding Y"
          value={paddingY}
          onChange={(v) => setProp((props: NavbarContainerProps) => (props.paddingY = v))}
          min={0}
          max={24}
          unit="px"
        />
      </SettingsRow>

      <SliderField
        label="Gap"
        value={gap}
        onChange={(v) => setProp((props: NavbarContainerProps) => (props.gap = v))}
        min={0}
        max={48}
        unit="px"
      />

      <SettingsRow>
        <ColorField
          label="Background"
          value={backgroundColor}
          onChange={(v) => setProp((props: NavbarContainerProps) => (props.backgroundColor = v))}
        />
        <ColorField
          label="Text Color"
          value={textColor}
          onChange={(v) => setProp((props: NavbarContainerProps) => (props.textColor = v))}
        />
      </SettingsRow>
    </SettingsSection>
  );
};

NavbarContainer.craft = {
  displayName: "Navbar Container",
  isCanvas: true,
  props: {
    backgroundColor: "#ffffff",
    textColor: "#1f2937",
    sticky: true,
    maxWidth: "1280px",
    paddingX: 24,
    paddingY: 12,
    gap: 16,
  },
  rules: {
    canDrag: () => true,
    canMoveIn: () => true,
  },
  related: {
    settings: NavbarContainerSettings,
  },
};
